import { useLayoutEffect, useRef, useState } from 'react'

export type ElementDimensions = {
  width: number
  height: number
  top: number
  left: number
}

export function useElementDimensions<T extends HTMLElement>(deps?: any[]) {
  const ref = useRef<T | null>(null)
  const [dimensions, setDimensions] = useState<ElementDimensions>({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
  })

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    let rafId: number | null = null

    const measure = () => {
      const rect = element.getBoundingClientRect()
      setDimensions({
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
      })
    }

    const scheduleMeasure = () => {
      if (rafId != null) return
      rafId = window.requestAnimationFrame(() => {
        rafId = null
        measure()
      })
    }

    // Initial måling
    measure()

    // Størrelsesændringer på elementet
    const resizeObserver = new ResizeObserver(() => {
      scheduleMeasure()
    })
    resizeObserver.observe(element)

    // Position kan ændre sig ved scroll/resize
    window.addEventListener('scroll', scheduleMeasure, { passive: true })
    window.addEventListener('resize', scheduleMeasure)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('scroll', scheduleMeasure)
      window.removeEventListener('resize', scheduleMeasure)
      if (rafId != null) window.cancelAnimationFrame(rafId)
    }
  }, deps ?? [])

  return { ref, dimensions }
}
